import { Ajax, Ajax2 } from '@shared/Ajax';
import { ILayerWrapperOptions, LayerWrapper } from '../LayerWrapper';
import { LogpointFeatureFactory } from './LogpointFeatureFactory';
import Feature, { FeatureLike } from 'ol/Feature';
import { LogpointsService } from '@shared/logpoints/LogpointsService';
import { makeObservable, observable } from 'mobx';
import { Notify } from '@shared/ui-native/Notify';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Layer from 'ol/layer/Layer';
import WebGLVectorLayerRenderer from 'ol/renderer/webgl/VectorLayer';
import { LogpointsLinecodeService } from '@shared/logpoints/LogpointsLinecodeService';
import { latLonToEN, WEBGL_NOT_HIDDEN_FILTER } from '@openlayers/ol-utils';
import { MultiLineString as OlMultiLineString } from 'ol/geom';

export class LogpointsLayer extends LayerWrapper<ILogpoint> {
    featureType: FeatureType = 'logpoint';

    private filter: ILogpointsFilter;
    private readonly ignoredFields = ['status', 'iconDimensions', 'iconImage'];
    private addedToMap = false;
    private linesLayerAdded = false;
    private readonly zIndex = 71;

    @observable public labelProps: string[] = ['dz'];
    @observable public iconScale = 8;
    @observable public showLines = true;
    @observable public noNamesInLabel = false;
    @observable public colorFunctionName: LogpointColorFunction = 'tolerance_approval';

    // cached value of smallest and largest timestamp in all of loaded logpoints set
    // user for color ramp style function, so we dont have to calculate it in each draw call
    private loadedTimestampRange = {
        min: 0,
        max: 1,
    };

    get isEmpty() {
        return this.layerSource.getFeatures().length === 0;
    }

    // we draw all point on webgl layer, and since it doesn't handle text, we draw labels on separate layer when zoomed in
    labelsLayer: VectorLayer<any> = null;
    highlightLayer: WebGLPointsLayer<any> = null;
    highlightLayerAddedToMap = false;
    linesLayer: WebGLLinesLayer = null;

    highlightFeatures = (features: Feature[]) => {
        if (features?.length) {
            if (!this.highlightLayer) {
                this.highlightLayer = new WebGLPointsLayer({
                    source: new VectorSource<any>(),
                    zIndex: this.zIndex - 1,
                    style: {
                        'circle-radius': 15,
                        'circle-fill-color': 'rgba(255, 255, 255, 0.8)',
                        'circle-stroke-width': 1.5,
                        'circle-stroke-color': 'rgba(71, 151, 204, 0.9)',
                    },
                });
                this.highlightLayer.set('id', 'logpoints_highlight_layer');
            }
            const source = this.highlightLayer.getSource() as VectorSource;
            source.clear(true);

            if (!this.highlightLayerAddedToMap) {
                this.map.addLayer(this.highlightLayer);
                this.highlightLayerAddedToMap = true;
            }
            source.addFeatures(features.map(f => f.clone()));
        } else if (this.highlightLayerAddedToMap) {
            const source = this.highlightLayer.getSource() as VectorSource;
            source.clear(true);
            this.map.removeLayer(this.highlightLayer);
            this.highlightLayerAddedToMap = false;
        }
    };

    constructor(opts: ILayerWrapperOptions<ILogpoint>) {
        super(opts);
        makeObservable(this);
        this.map = opts.map;

        this.layerSource.on('addfeature', this.onLayerSourceChanged);
        this.layerSource.on('removefeature', this.onLayerSourceChanged);

        setTimeout(() => this.triggerLayerChange(), 200);
    }

    addLayersToMap = () => {
        if (this.addedToMap) return;
        this.addedToMap = true;

        if (this.layer == null) {
            this.layer = new WebGLPointsLayer({
                source: this.layerSource,
                zIndex: this.zIndex,
                filter: WEBGL_NOT_HIDDEN_FILTER,
                style: LogpointFeatureFactory.getWebGlStyle(parseFloat((this.iconScale * 0.07).toFixed(1)), this.colorFunctionName),
            });
            this.layer.set('id', this.featureType);
        }

        this.map.addLayer(this.layer);
    };

    removeLayersFromMap = () => {
        if (!this.addedToMap) return;
        this.addedToMap = false;

        if (this.layer) {
            // dispose() can throw when webgl context is lost
            try {
                this.map.removeLayer(this.layer);
                this.layer.dispose();
            } catch {}
            this.layer = null;
        }

        if (this.highlightLayer) {
            try {
                this.map.removeLayer(this.highlightLayer);
                this.highlightLayer.dispose();
            } catch {}
            this.highlightLayer = null;
            this.highlightLayerAddedToMap = false;
        }

        if (this.linesLayer) {
            try {
                this.map.removeLayer(this.linesLayer);
                this.linesLayer.dispose();
            } catch {}
            this.linesLayer = null;
            this.linesLayerAdded = false;
        }
    };

    reinitLayer = () => {
        if (this.isEmpty) return;

        this.removeLayersFromMap();
        this.addLayersToMap();
    };

    onLayerSourceChanged = _.debounce(() => {
        const features = this.layerSource.getFeatures();
        if (features.length === 0) {
            this.removeLayersFromMap();
        } else if (!this.addedToMap) {
            this.addLayersToMap();
        }

        // used for heatmap coloring
        const timestamps = this.getVisibleFeatures()
            .map(feature => feature.get('data').timestamp)
            .filter(Boolean)
            .sort();
        this.loadedTimestampRange.min = timestamps[0] ?? 0;
        this.loadedTimestampRange.max = timestamps[timestamps.length - 1] ?? 0;

        this.drawLines();
        this.layerSource
            .getFeatures()
            .forEach(f => LogpointFeatureFactory.recomputeFeatureProps(f, this.colorFunctionName, this.loadedTimestampRange.min, this.loadedTimestampRange.max));
        this.triggerLayerChange();
        this.forceRerenderMap();
    }, 20);

    applyFilter = _.debounce((filter: ILogpointsFilter) => {
        this.filter = filter;
        this.setHiddenOnFeatures(this.layerSource.getFeatures());
        this.onLayerSourceChanged(); // lines should respect the filter, IN-5358
    }, 300);

    triggerLayerChange = _.debounce(() => {
        this.layerSource.dispatchEvent('change');
        const features = this.layerSource.getFeatures();
        if (features[0]) {
            // webgl layers wont refresh with layerSource.dispatchEvent('change'), so this hack is needed
            features[0].dispatchEvent('change');
        } else {
            this.layerSource.refresh();
        }
    }, 20);

    postProcessFeature(feature: Feature): void {
        if (feature) {
            // numeric 0/1 because the webgl style filter reads it as a float attribute
            feature.set('hidden', this.isFeatureVisible(feature) ? 0 : 1, true);
            LogpointFeatureFactory.recomputeFeatureProps(feature, this.colorFunctionName, this.loadedTimestampRange.min, this.loadedTimestampRange.max);
        }
    }

    featureMatchesFilter(feature: Feature): boolean {
        if (feature.get('forceShow')) {
            return true;
        }

        const data = this.getAjaxData(feature);
        if (data.code == null) data.code = '';

        if (!LogpointsService.matchesFilter(data, this.filter)) {
            return false;
        }

        return true;
    }

    isFeatureVisible(feature: FeatureLike): boolean {
        if (feature.get('forceShow')) {
            return true;
        }

        const data = this.getAjaxData(feature);
        if (data.code == null) data.code = '';

        // check ui filtering
        if (!LogpointsService.matchesFilter(data, this.filter)) {
            return false;
        }

        return true;
    }

    getVisibleFeatures(): Feature[] {
        return this.layerSource.getFeatures().filter(f => this.isFeatureVisible(f));
    }

    setHiddenOnFeatures = (features: Feature[]) => {
        features.forEach(f => {
            // numeric 0/1 because the webgl style filter reads it as a float attribute
            const newState = this.isFeatureVisible(f) ? 0 : 1;
            if (newState !== f.get('hidden')) {
                f.set('hidden', newState, true);
            }
        });
        this.triggerLayerChange();
    };

    fetchAndDrawFolder(ajaxData: any): JQueryPromise<{
        features: Feature[];
        failedToCreate: any[];
    }> {
        return Ajax({
            url: `ajax_logpoints.json`,
            data: {
                id: ajaxData,
                ignoreFields: this.ignoredFields,
                r: false, // recurse, fetch from sub folders or only first level of folder
            },
        }).then((resp: ILogpointsAjaxResponse) => {
            resp.logpoints.forEach((l, i) => {
                l.folderId = ajaxData;
                l.ordinalInSource = i;
            });
            return this.drawAll(resp.logpoints);
        });
    }

    fetchAndDrawFile(ajaxData?: any): JQueryPromise<any> {
        return Ajax({
            url: `api/1/logpoint/model_${ajaxData}.json`,
            data: {
                ignoreFields: this.ignoredFields,
            },
        }).done((resp: ILogpointsAjaxResponse) => {
            resp.logpoints.forEach((l, i) => {
                l.ordinalInSource = i;
            });
            return this.drawAll(resp.logpoints);
        });
    }

    fetchAndDrawSingle(logpointId: number): JQueryPromise<any> {
        return Ajax({
            url: 'ajax_logpoint.json',
            data: {
                logpointId,
            },
        }).done((resp: ILogpointWithApprovalsAjaxResponse) => {
            this.removeFeatureById(logpointId);
            this.drawOne(resp.ajaxLogpointData);
        });
    }

    fetchAndDrawMultiple(logpointIdList: number[]): Promise<{
        features: Feature[];
        failedToCreate: ILogpoint[];
    }> {
        if (logpointIdList?.length == 0) {
            return Promise.resolve({ features: [], failedToCreate: [] });
        }

        return Ajax2({
            url: kuura.getContextPath() + '/api/ajax_logpoint_list.json',
            method: 'POST',
            contentType: 'json',
            data: {
                logpointIdList: logpointIdList,
            },
        }).then((resp: ILogpointsAjaxResponse) => {
            const result = this.drawAll(resp.logpoints);
            return result;
        });
    }

    setLabelProps(props: string | string[]) {
        this.labelProps = Array.isArray(props) ? props : [props];
        if (this.labelProps.length === 0 && this.labelsLayer) {
            this.map.removeLayer(this.labelsLayer);
            this.labelsLayer.dispose();
            this.labelsLayer = null;
        } else if (!this.labelsLayer) {
            if (this.labelsLayer == null) {
                this.labelsLayer = new VectorLayer({
                    source: this.layerSource,
                    updateWhileAnimating: false,
                    updateWhileInteracting: false,
                    //declutter: true,
                    minZoom: 16,
                    zIndex: this.zIndex - 1,
                });
                this.labelsLayer.set('id', this.featureType + '_labels');

                this.labelsLayer.setStyle(feature => {
                    if (feature.get('hidden')) return null;
                    const l: ILogpoint = feature.get('data');
                    const label = LogpointFeatureFactory.getLogpointLabel(l, this.labelProps, this.noNamesInLabel);
                    if (!label || label == '') return null;

                    let color = 'black';
                    if (this.colorFunctionName === 'tolerance') color = LogpointFeatureFactory.colorFunctions.tolerance(l);
                    else if (this.colorFunctionName === 'tolerance_approval') color = LogpointFeatureFactory.colorFunctions.tolerance_approval(l);
                    else if (this.colorFunctionName === 'timestamp_color_ramp') {
                        color = LogpointFeatureFactory.colorFunctions.timestamp_color_ramp(l, this.loadedTimestampRange.min, this.loadedTimestampRange.max);
                    }
                    return [LogpointFeatureFactory.renderLabelIconStyle(label, color)];
                });
            }

            this.map.addLayer(this.labelsLayer);
        }
        this.layerSource.dispatchEvent('change');
    }

    toggleLabelProp(prop: string) {
        const props = new Set(this.labelProps);
        if (props.has(prop)) {
            props.delete(prop);
        } else {
            if (this.labelProps.length >= 3) {
                Notify.warn('Up to 3 labels are allowed');
                return;
            }
            props.add(prop);
        }
        this.labelProps = Array.from(props);
        this.layerSource.dispatchEvent('change');
    }

    public setIconScale(scale: number) {
        this.iconScale = utils.clamp(8, scale, 15);
        this.reinitLayer();
        this.layerSource.dispatchEvent('change');
    }

    public setNoNamesInLabels(value: boolean) {
        this.noNamesInLabel = value;
        this.layerSource.dispatchEvent('change');
    }

    public setColorFunction = (colorFunction: LogpointColorFunction) => {
        this.colorFunctionName = colorFunction;
        this.reinitLayer();
        this.layerSource.dispatchEvent('change');
    };

    public setShowLines = (state: boolean) => {
        this.showLines = state;
        this.drawLines();
    };

    public toggleShowLines = () => {
        this.setShowLines(!this.showLines);
    };

    private drawLines = () => {
        this.makeLineFeatures(this.showLines ? this.getVisibleFeatures() : []);
    };

    clear() {
        this.layerSource.clear(true);
        if (this.highlightLayer) {
            this.highlightLayer.getSource().clear(true);
        }
        if (this.linesLayer) {
            (this.linesLayer.getSource() as VectorSource).clear(false);
        }

        this.removeLayersFromMap();
    }

    makeLineFeatures = _.debounce((logpointFeatures: Feature[]) => {
        if (this.linesLayer) {
            (this.linesLayer.getSource() as VectorSource).clear(true);
        }

        if (logpointFeatures.length === 0) {
            if (this.linesLayer) {
                this.map.removeLayer(this.linesLayer);
                this.linesLayer.dispose();
                this.linesLayer = null;
            }
            this.linesLayerAdded = false;
            return;
        }

        if (!this.linesLayerAdded) {
            if (this.linesLayer == null) {
                this.linesLayer = new WebGLLinesLayer({
                    source: new VectorSource(),
                    zIndex: this.zIndex - 1,
                });
            }
            this.map.addLayer(this.linesLayer);
            this.linesLayerAdded = true;
        }

        let withLinecode: ILogpoint[] = [];

        for (const f of logpointFeatures) {
            const data: ILogpoint = f.get('data');
            if (
                data.hasOwnProperty('linecode') &&
                data.linecode !== null &&
                data.linecode.length > 0 &&
                data.linecode !== '0' // IN-3110, see comments
            ) {
                const source: FeatureSource = f.get('source');
                data.loadSource = LogpointsLinecodeService.getLoadSourceAsString(source);
                withLinecode.push(data);
            }
        }
        if (withLinecode.length === 0) return;

        withLinecode = JSON.parse(JSON.stringify(withLinecode)); // need to deep copy logpoint

        const sorted = LogpointsLinecodeService.sortLogpoints(withLinecode);
        const sortedCoords = sorted.map(line => line.map(l => latLonToEN(l.lat, l.lon)));

        const lineFeature = new Feature({
            geometry: new OlMultiLineString(sortedCoords),
        });

        lineFeature.setId('linecodeLines');
        (this.linesLayer.getSource() as VectorSource).addFeature(lineFeature);
    }, 50);
}

class WebGLLinesLayer extends Layer {
    createRenderer() {
        return new WebGLVectorLayerRenderer(this, {
            style: {
                'stroke-width': 2,
                'stroke-color': [0, 0, 0, 0.7],
            },
            variables: {},
        });
    }
}
